import {
  Button,
  SingleFieldDialog,
  SingleFieldDialogHeader,
  SingleFieldDialogContent,
  SingleFieldDialogCTA,
} from "@sellar-io/components";
import { useState } from "react";
import { useBasketContext } from "../../../hooks";
import { useStorefrontPromotion } from "../categoryPage/Storefront/StorefrontPromotion/StorefrontPromotionProvider.shared";
import {
  capitalize,
  isOrderItemEligibleForDeliveryMethod,
  pluralize,
} from "@sellar-io/common";
import {
  DeliveryMethodType,
  isValidContainerUnit,
} from "./DeliveryMethodRadioSelector.shared";
import { CheckoutSelectedFulfillmentType } from "..";

export const IneligibleItemsDialog = ({
  active,
  onDismiss,
  targetFulfillmentType,
  targetFulfillmentTypeData,
  onConfirm,
}: {
  active: boolean;
  onDismiss: () => void;
  targetFulfillmentType: CheckoutSelectedFulfillmentType;
  targetFulfillmentTypeData: DeliveryMethodType | null;
  onConfirm: () => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [{ order, modifyOrderItem }] = useBasketContext();
  const { applyEligiblePromotionRewards, claimedPromotionRewardItemIds } =
    useStorefrontPromotion();

  const ineligibleItems = order?.OrderItems?.filter((item) => {
    if (!targetFulfillmentTypeData) return false;

    const product = item.Product;

    if (!isValidContainerUnit(product.unit)) return true;

    return !isOrderItemEligibleForDeliveryMethod({
      deliveryMethod: targetFulfillmentTypeData,
      item: {
        containerType: product.containerType,
        unit: product.unit,
        volume: product.volume,
      },
    });
  });

  const onRemove = () => {
    setLoading(true);
    ineligibleItems?.forEach((item) => {
      modifyOrderItem?.({
        productItem: {
          ...item.Product,
          ProductPriceTier: item.Product.ProductPriceTier
            ? {
                ...item.Product.ProductPriceTier,
                fullPrice: item.fullPrice,
              }
            : null,
        },
        quantity: 0,
        applyEligiblePromotionRewards: ({ order }) => {
          return applyEligiblePromotionRewards({
            order,
            claimedPromotionRewardItemIds,
          });
        },
      });
    });

    onConfirm();
    setLoading(false);
    onDismiss();
  };

  const targetFulfillmentTypeLabel =
    targetFulfillmentType === "haulier"
      ? "Pallet"
      : capitalize(targetFulfillmentType ?? "");

  return (
    <SingleFieldDialog active={active} onDismiss={onDismiss} size="large">
      <SingleFieldDialogHeader
        title={`Some items in your basket aren't available with ${targetFulfillmentTypeLabel}`}
        description={
          <span className="text-base text-zinc-500">
            Your basket contains {ineligibleItems?.length}{" "}
            {pluralize("item", ineligibleItems?.length)} that can't be delivered
            by {capitalize(targetFulfillmentType ?? "")}. You can remove these
            items and switch to {capitalize(targetFulfillmentType ?? "")}{" "}
            delivery, or keep your current basket and delivery method
          </span>
        }
        icon="ALERT"
        iconClassName="bg-yellow-50 text-yellow-600"
      />
      <SingleFieldDialogContent className="px-4">
        <div className="flex flex-col gap-4 rounded-lg bg-zinc-100 p-4 text-sm">
          <span className="font-semibold">Item(s) to be removed:</span>
          <ul className="list-disc pl-4">
            {ineligibleItems?.map((item) => {
              const nameParts = item.Product.name?.split(" • ");
              return <li key={item.id}>{nameParts?.join(" - ")}</li>;
            })}
          </ul>
        </div>
      </SingleFieldDialogContent>
      <SingleFieldDialogCTA className="flex flex-col gap-2 space-x-0">
        <Button
          color="red"
          onClick={onRemove}
          loading={loading}
          className="w-full"
        >
          Remove items and switch to {capitalize(targetFulfillmentType ?? "")}
        </Button>
        <Button color="secondary" onClick={onDismiss} className="w-full">
          Keep items and current delivery method
        </Button>
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};
